import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchMyProfile } from '../api/auth';
import { refreshAccessToken } from '../api/client';
import { getTokenStore } from '../api/token-store';
import { useAuthStore } from '../stores/auth-store';
import type { UserRole } from '../types/api';

export const ME_QUERY_KEY = ['me'] as const;

export function useMe() {
  const setSession = useAuthStore((s) => s.setSession);
  const setHydrated = useAuthStore((s) => s.setHydrated);
  const [hasToken, setHasToken] = useState(() => !!getTokenStore().getAccessToken());
  const [refreshAttempted, setRefreshAttempted] = useState(false);

  useEffect(() => {
    const currentToken = getTokenStore().getAccessToken();
    if (currentToken) {
      setHasToken(true);
      return;
    }
    if (!refreshAttempted) {
      setRefreshAttempted(true);
      refreshAccessToken()
        .then((token) => {
          if (token) {
            setHasToken(true);
          } else {
            setHydrated(true);
          }
        })
        .catch(() => {
          setHydrated(true);
        });
    }
  }, [refreshAttempted, setHydrated]);

  const query = useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: fetchMyProfile,
    enabled: hasToken,
    staleTime: 60_000,
    retry: 1,
  });

  useEffect(() => {
    if (query.data) setSession(query.data);
    if (!hasToken && refreshAttempted) setSession(null);
    if (query.isFetched || (!hasToken && refreshAttempted)) setHydrated(true);
  }, [query.data, query.isFetched, hasToken, refreshAttempted, setSession, setHydrated]);

  return query;
}

// Root-level hydration: mount once at the top of <App /> so the session is
// restored from persisted tokens on page load — before any route-specific
// logic runs. Without this, public routes (landing/login) render as logged-out
// on refresh even though valid tokens sit in localStorage.
export function AuthBoot() {
  useMe();
  return null;
}

export function roleHome(role: UserRole): string {
  if (role === 'ASSOCIATE' || role === 'FRANCHISE') return '/associate/transactions';
  if (role === 'ADMIN') return '/';
  return '/client/home';
}

// Used by public pages (landing, login, associate/join) to bounce an already-
// authed user to their dashboard. `waiting` is true only while we're restoring
// a session from a stored token — so we can show a brief loader instead of a
// flash of logged-out UI before the redirect.
export function usePublicRedirect(): { target: string | null; waiting: boolean } {
  const session = useAuthStore((s) => s.session);
  const hydrated = useAuthStore((s) => s.hydrated);
  const hasToken = !!getTokenStore().getAccessToken();

  if (!hasToken) return { target: null, waiting: false };
  if (!hydrated) return { target: null, waiting: true };
  if (session && session.role !== 'ADMIN') {
    return { target: roleHome(session.role), waiting: false };
  }
  return { target: null, waiting: false };
}
